import { PrimaryButton, Stack, Text } from "@certn/cascadia";
import {
  Card,
  Container,
  Divider,
  Icon,
  InnerContentItem,
  Layout,
  MediaQuery,
  mq,
  SectionMessage,
  StyledIcon,
  SummaryCard,
} from "@certn/cascadia-react";
import { ProgressStepper } from "@certn/cascadia-react/components/stepper/ProgressStepper";
import { CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { ComponentProps } from "react";
import { Trans, useTranslation } from "react-i18next";

import { AppFooter } from "../../components/app-footer";
import { AppHeader } from "../../components/app-header";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";
import { Schemas } from "../../types";

interface Props {
  credentialRequest: Schemas["CaseCredentialRequest"];
}

export function ApplicationSubmittedContinueInApp(props: Props) {
  const { t } = useTranslation();
  useDocumentTitle(t("Complete your application — Certn"));

  const { credentialRequest } = props;

  const verifier =
    credentialRequest.verifier?.customer_group?.applicant_facing_name || "-";

  return (
    <Layout
      menuPosition="left"
      layout="applicant/narrow"
      header={<AppHeader />}
      footer={<AppFooter />}
    >
      <Container>
        <Stack gap="space-2">
          <Text as="h1" style="form/page-title">
            <Trans>To complete your application...</Trans>
          </Text>
          <Text style="form/body-primary" color="text-weak">
            <Trans>
              <strong>{{ verifier } as any}</strong> has requested further
              information from you. Please download the MyCertn app to complete
              your application.
            </Trans>
          </Text>
        </Stack>
        <Card
          background="background-selected-weak"
          padding="space-6"
          className={css({ marginBottom: "var(--space-4)" })}
        >
          <Stack gap="space-6">
            <MediaQuery sm type="show">
              <Text style="form/page-title">
                {t("Download the MyCertn app")}
              </Text>
            </MediaQuery>
            <MediaQuery sm type="hide">
              <Text style="form/page-title">
                {t("Scan the QR to Download the MyCertn app")}
              </Text>
            </MediaQuery>
            <MediaQuery sm type="show">
              <PrimaryButton
                fullWidth
                onPress={() => {
                  window.open("https://wallet.certn.co", "_blank");
                }}
              >
                {t("Get the app")}
              </PrimaryButton>
            </MediaQuery>
            <Stack gap="space-0">
              <div className={styles.downloadAppContainer}>
                <Stack gap="space-4">
                  <MediaQuery sm type="hide">
                    <a href="wallet.certn.co">
                      <img
                        src="/images/mycertn-qr.png"
                        width="150"
                        height="150"
                        alt="App Store"
                      />
                    </a>
                  </MediaQuery>
                  <Stack orientation="horizontal" gap="space-3">
                    <a href="https://apps.apple.com/us/app/mycertn/id6449458498">
                      <img
                        height="32"
                        src="/images/appstore.png"
                        alt="App Store"
                      />
                    </a>
                    <a href="https://play.google.com/store/apps/datasafety?id=co.certn.wallet">
                      <img
                        height="32"
                        src="/images/playstore.png"
                        alt="App Store"
                      />
                    </a>
                  </Stack>
                </Stack>
                <img width="280" src="/images/mycertn.png" alt="App Store" />
              </div>
              <Divider />
            </Stack>
            <Stack>
              <Stack orientation="horizontal" gap="space-2" align="start">
                <Icon
                  type="mini"
                  color="icon-accent-tofino"
                  name="check"
                  size="space-6"
                />
                <Text style="form/body-primary" color="text-weak">
                  <Trans>
                    Verify your identity, credit score, and income for{" "}
                    <strong>free</strong>
                  </Trans>
                </Text>
              </Stack>
              <Stack orientation="horizontal" gap="space-2" align="start">
                <Icon
                  type="mini"
                  color="icon-accent-tofino"
                  name="check"
                  size="space-6"
                />
                <Text style="form/body-primary" color="text-weak">
                  <Trans>
                    <strong>Stand out</strong> as a top-tier applicant in a
                    competitive rental market
                  </Trans>
                </Text>
              </Stack>
              <Stack orientation="horizontal" gap="space-2" align="start">
                <Icon
                  type="mini"
                  color="icon-accent-tofino"
                  name="check"
                  size="space-6"
                />
                <Text style="form/body-primary" color="text-weak">
                  <Trans>
                    Share your verifiable information{" "}
                    <strong>safely and securely</strong>.
                  </Trans>
                </Text>
              </Stack>
              <Stack orientation="horizontal" gap="space-2" align="start">
                <Icon
                  type="mini"
                  color="icon-accent-tofino"
                  name="check"
                  size="space-6"
                />
                <Text style="form/body-primary" color="text-weak">
                  <Trans>
                    <strong>No more repetitive form-filling</strong>: verify
                    once and use your profile for multiple applications.
                  </Trans>
                </Text>
              </Stack>
            </Stack>
          </Stack>
        </Card>
        <SummaryCard header={t("Request details")}>
          <InnerContentItem>
            <SectionMessage
              type="info"
              icon="mail"
              sectionTitle={t(
                "An invitation email has also been sent to your inbox."
              )}
            />
          </InnerContentItem>
          <InnerContentItem label={t("Requestor:")}>
            {verifier}
          </InnerContentItem>
          {credentialRequest.custom_message && (
            <InnerContentItem label={t("Details:")}>
              {credentialRequest.custom_message}
            </InnerContentItem>
          )}
          <InnerContentItem label={t("Required credentials:")}>
            <Stack
              gap="space-2"
              className={css({ paddingTop: "var(--space-4)" })}
            >
              {credentialRequest.requested_credential_definitions?.map(
                (definition) => (
                  <Card key={definition.kind}>
                    <Stack
                      orientation="horizontal"
                      className={styles.credentialCard}
                      gap="space-3"
                    >
                      <StyledIcon
                        type="standard"
                        name={definition.icon as CascadiaStandardIcon}
                        tint={
                          definition.color as ComponentProps<
                            typeof StyledIcon
                          >["tint"]
                        }
                      />
                      <Text style="global/heading-medium">
                        {definition.name}
                      </Text>
                    </Stack>
                  </Card>
                )
              )}
            </Stack>
          </InnerContentItem>
        </SummaryCard>
        <Card
          background="background-accent-green-weakest"
          padding="space-4"
          hideBorder
        >
          <Stack gap="space-9" className={styles.nextSteps}>
            <Stack gap="space-2">
              <Text style="form/page-title">{t("How to get started")}</Text>
              <Text style="form/body-primary" color="text-weak">
                {t("Get started on your credentials today in 3 easy steps!")}
              </Text>
            </Stack>
            <Stack gap="space-0">
              <ProgressStepper
                steps={[
                  {
                    status: "ACTIVE",
                    title: t("Download app"),
                    description: t(
                      "Available on the App Store or Google Play."
                    ),
                  },
                  {
                    status: "ACTIVE",
                    title: t("Register & set up account"),
                    description: t(
                      "Complete the registration process in seconds."
                    ),
                  },
                  {
                    status: "ACTIVE",
                    title: t("Claim credentials"),
                    description: t(
                      "You will automatically be prompted to claim your credentials."
                    ),
                  },
                ]}
              />
            </Stack>
          </Stack>
        </Card>
      </Container>
    </Layout>
  );
}

const styles = {
  downloadAppContainer: css(
    { "> *": { display: "flex", flex: 1 } },
    mq({
      flexDirection: ["column", "row"],
      display: "flex",
      gap: ["var(--space-4)", 0],
      alignItems: ["center", "flex-end"],
    })
  ),
  credentialCard: css({ padding: "var(--space-4)" }),
  credentialIcon: css({
    backgroundColor: "var(--background-accent-neutral-weak)",
    padding: "var(--space-2)",
    borderRadius: "var(--border-radius-2)",
    display: "flex",
    alignContent: "center",
    justifyContent: "center",
  }),
  nextSteps: css({
    paddingTop: "var(--space-2)",
    paddingBottom: "var(--space-2)",
  }),
};
